import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { SITE, OG_IMAGE, stripLocale } from "@/lib/seo";

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const upsertLink = (rel, href, hreflang) => {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(sel);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); if (hreflang) el.setAttribute("hreflang", hreflang); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

/**
 * Route-level SEO. title/description accept {en,ar} or string. schema = JSON-LD object(s).
 */
export const Seo = ({ title, description, schema, image, type = "website" }) => {
  const { t, lang } = useLang();
  const { pathname } = useLocation();

  useEffect(() => {
    const bare = stripLocale(pathname);
    const canonical = `${SITE}/${lang}${bare === "/" ? "" : bare}`;
    const ttl = t(title);
    const desc = t(description);
    const img = image || OG_IMAGE;

    if (ttl) document.title = ttl;
    upsertMeta("name", "description", desc);
    upsertMeta("name", "robots", "index, follow, max-image-preview:large");
    upsertLink("canonical", canonical);
    // hreflang alternates
    upsertLink("alternate", `${SITE}/en${bare === "/" ? "" : bare}`, "en-AE");
    upsertLink("alternate", `${SITE}/ar${bare === "/" ? "" : bare}`, "ar-AE");
    upsertLink("alternate", `${SITE}/en${bare === "/" ? "" : bare}`, "x-default");
    // open graph / twitter
    upsertMeta("property", "og:site_name", "Smart Account Vision Tax Consultant");
    upsertMeta("property", "og:title", ttl);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:locale", lang === "ar" ? "ar_AE" : "en_AE");
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", ttl);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", img);

    // JSON-LD (route-scoped, id-tagged so we can replace it)
    const prev = document.getElementById("route-jsonld");
    if (prev) prev.remove();
    if (schema) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "route-jsonld";
      s.text = JSON.stringify(schema);
      document.head.appendChild(s);
    }
  }, [pathname, lang, title, description, schema, image, type, t]);

  return null;
};
